import React, { useMemo, useState } from 'react';
import { Stage, Layer, Group, Line, Text, Circle } from 'react-konva';
import { Box, Typography } from '@mui/material';
import { DoorRenderer } from '../DoorRenderer';
import { WindowRenderer } from '../WindowRenderer';
import { ElectricityPointRenderer } from '../ElectricityPointRenderer';
import {
  deriveRoomGeometry,
  computePointsFromWalls,
  calculateWallLength,
  WALL_RENDER_PROPERTIES
} from '../../dataModels';

export function RoomPreview({
  room,
  selectedElementId,
  onWallClick,
  onDoorClick,
  onWindowClick,
  onElectricityPointClick
}) {
  // Keep preview size stable
  const previewSize = 400;

  const walls = room?.walls || [];

  const { centerX, centerY, area } = useMemo(() => {
    return deriveRoomGeometry(walls);
  }, [walls]);

  const { minX, maxX, minY, maxY } = useMemo(() => {
    // computePointsFromWalls should return bounds; if it returns points,
    // rename it or adjust. Here we assume it returns {minX,maxX,minY,maxY}.
    return computePointsFromWalls(walls);
  }, [walls]);

  if (!room || walls.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          אין קירות בחדר
        </Typography>
      </Box>
    );
  }



  const roomWidth = maxX - minX;
  const roomHeight = maxY - minY;

  // Fit-to-view scale with padding
  const scale = Math.min(
    (previewSize * 0.8) / Math.max(roomWidth, 1),
    (previewSize * 0.8) / Math.max(roomHeight, 1),
    50
  );

  // Camera translate so centroid is centered
  const offsetX = previewSize / 2 - centerX * scale;
  const offsetY = previewSize / 2 - centerY * scale;

  // -----------------------------
  // Helpers
  // -----------------------------


  const renderWall = (wall) => {
    const props =
      WALL_RENDER_PROPERTIES?.[wall.type] ||
      WALL_RENDER_PROPERTIES?.default || {
        strokeColor: '#666',
        fillColor: null,
        strokePattern: null,
        thickness: 0.2
      };

    const { strokeColor, fillColor, strokePattern, thickness } = props;
    const { startPoint, endPoint } = wall;

    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const len = Math.hypot(dx, dy) || 1;

    const perpX = (-dy / len) * (thickness / 2);
    const perpY = (dx / len) * (thickness / 2);

    const wallLength = calculateWallLength(wall);
    const isSelected = selectedElementId === wall.id;

    return (
      <Group key={wall.id}>
        {/* Always render fill polygon for clickable area, even if transparent */}
        <Line
          points={[
            startPoint.x + perpX, startPoint.y + perpY,
            endPoint.x + perpX, endPoint.y + perpY,
            endPoint.x - perpX, endPoint.y - perpY,
            startPoint.x - perpX, startPoint.y - perpY
          ]}
          closed
          fill={fillColor || 'transparent'}
          stroke={fillColor ? strokeColor : 'transparent'}
          hitStrokeWidth={0.5}
          strokeScaleEnabled={false}
          dash={strokePattern || undefined}
          onClick={(e) => {
            e.cancelBubble = true;
            onWallClick(wall, e);
          }}
        />
        <Line
          points={[
            startPoint.x,
            startPoint.y,
            endPoint.x,
            endPoint.y
          ]}
          stroke={isSelected ? '#2196F3' : strokeColor}
          strokeWidth={thickness}
          strokeScaleEnabled={false}
          hitStrokeWidth={0.5}
          dash={strokePattern || undefined}
          onClick={(e) => {
            e.cancelBubble = true;
            onWallClick(wall, e);
          }}
        />

        {wallLength > 0.5 && (
          <Text
            x={(startPoint.x + endPoint.x) / 2 - 0.20}
            y={(startPoint.y + endPoint.y) / 2 - 0.10}
            text={`${wallLength.toFixed(2)}m`}
            fontSize={0.10}
            fill="#666"
            listening={false}
          />
        )}
      </Group>
    );
  };

  const renderDoors = (doors = [], wall) => {
    return doors.map((d) => {
      const isSelected = selectedElementId === d.id;
        
      return (
        <Group
          key={d.id}
          opacity={isSelected ? 0.7 : 1}
          onClick={(e) => {
            e.cancelBubble = true;
            onDoorClick(d, e);
          }}
        >
          <DoorRenderer door={d} wall={wall} roomCenter={{ x: centerX, y: centerY }} />
        </Group>
      );
    });
  };

  const renderWindows = (windows = [], wall) => {
    return windows.map((w) => {
      const isSelected = selectedElementId === w.id;
      return (
        <Group
          key={w.id}
          opacity={isSelected ? 0.7 : 1}
          onClick={(e) => {
            e.cancelBubble = true;
            onWindowClick(w, e);
          }}
        >
          <WindowRenderer window={w} wall={wall} />
        </Group>
      );
    });
  };

  const renderElectricityPoints = (points = [], wall) => {
    return points.map((p) => {
      const isSelected = selectedElementId === p.id;
      return (
        <Group
          key={p.id}
          opacity={isSelected ? 0.7 : 1}
          onClick={(e) => {
            e.cancelBubble = true;
            onElectricityPointClick(p, e);
          }}
        >
          <ElectricityPointRenderer point={p} wall={wall} roomCenter={{ x: centerX, y: centerY }} />
        </Group>
      );
    });
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      <Stage width={previewSize} height={previewSize}>
        <Layer>
          {/* Camera group ONLY (allowed) */}
          <Group x={offsetX} y={offsetY} scaleX={scale} scaleY={scale} key={room.id}>
            {/* Walls + elements anchored to each wall */}
            {walls.map((w) => (
              <React.Fragment key={w.id}>
                {renderWall(w)}
                {renderDoors(w.doors || [], w)}
                {renderWindows(w.windows || [], w)}
                {renderElectricityPoints(w.electricityPoints || [], w)}
              </React.Fragment>
            ))}

            {room.classification && (
              <Text
                x={centerX - 1.5}     // half of labelWidth
                y={centerY - 0.25}
                width={3}             // meters (enough for text)
                text={room.classification}
                fontSize={0.10}
                fill="#555"
                align="center"
                listening={false}
              />
            )}

            {/* Room number */}
            {room.number != null && room.number !== '' && (
              <>
                <Circle
                  x={centerX}
                  y={centerY}
                  radius={0.15}
                  fill="#fff"
                  stroke="#333"
                  strokeWidth={0.02}
                  strokeScaleEnabled={false}
                  listening={false}
                />
                <Text
                  x={centerX - 0.08}
                  y={centerY - 0.08}
                  text={room.number.toString()}
                  fontSize={0.12}
                  fill="#333"
                  listening={false}
                />
              </>
            )}

            {/* Room area */}
            {area > 0 && (
              <Text
                x={centerX - 0.30}
                y={centerY + 0.20}
                text={`${area.toFixed(2)} m²`}
                fontSize={0.10}
                fill="#666"
                listening={false}
              />
            )}
          </Group>
        </Layer>
      </Stage>
    </Box>
  );
}